import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  addOrReplaceAndRunAnalysisStep,
  copyAnalysisViewSteps,
  deleteAnalysisSteps,
  DeletePipelineStepsBehavior,
  updateAndRunAnalysisStep,
} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_SAMPLED_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES} from 'analysis-tool/AnalysisToolConstants';
import {
  findAndAssertSingleStepDataFrameId,
  findAndAssertStepData,
  findAndAssertOutputStepData,
  findSingleStepOutputDataFrameId,
} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import getStepVisualizationConfigAndName from 'analysis-tool/data/getVisualizationConfigAndName';
import QueryingChartAndDataFrame from 'analysis-tool/ui/main-view/QueryingChartAndDataFrame';
import {assert} from 'Asserts';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VISUALIZATION_TYPE_TO_FLEX_GROW} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {getTableLineageAncestorSteps} from 'pipelines/StepLineageUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import LoadingOverlay from 'ui-library/LoadingOverlay';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import {omit} from 'util/omitKey';
import {withLoadingScreen} from 'util/Promises';

import {getPersistedChartViewConfig} from '../charting/getPersistedVisualizationConfig';
import VisualizationBlock from '../charting/VisualizationBlock';
import StepLockedWarningBanner from '../components/StepLockedWarningBanner';
import {getSaveToResultsOverflowOption} from './MainViewHelpers';
import {AnalysisMainViewItemType, MainViewComponentProps} from './MainViewTypes';

/**
 * Component to show a view created by a `CHART_VIEW` or `TABLE_VIEW` analysis step
 *
 * @package
 */
export default function ChartView({
  clientStateId,
  analysis,
  onChangeSelectedItem,
  onOpenStepInitializationView,
  step: chartOrTableStep,
}: MainViewComponentProps<AnalysisMainViewItemType.CHART>): JSX.Element | null {
  const {visualizationConfig, viewName} = getStepVisualizationConfigAndName(chartOrTableStep);
  const visualizationType = getVisualizationType(visualizationConfig);
  const viewSteps = getTableLineageAncestorSteps(analysis, chartOrTableStep);
  const inputStepData = findAndAssertStepData(chartOrTableStep, SINGLE_DATASET_INPUT_STEP_DATA_NAME, true);
  if (inputStepData.dataFrames.length === 0) {
    return <CenteredHorizontalThrobber />;
  }

  const chartId =
    chartOrTableStep.statusCode === PipelineStepStatusCode.SUCCEEDED &&
    chartOrTableStep.definition.stepType === PipelineStepType.CHART_VIEW
      ? assertIsAccessible(
          findAndAssertOutputStepData(chartOrTableStep, SINGLE_CHART_OUTPUT_STEP_DATA_NAME).charts[0]
        ).id
      : undefined;

  const inputDataFrameId = findAndAssertSingleStepDataFrameId(
    chartOrTableStep,
    SINGLE_DATASET_INPUT_STEP_DATA_NAME,
    true
  );

  const sampledOutputDataFrameId =
    chartOrTableStep.statusCode === PipelineStepStatusCode.FAILED || PipelineStepType.TABLE_VIEW
      ? null
      : findSingleStepOutputDataFrameId(chartOrTableStep, SINGLE_DATASET_SAMPLED_OUTPUT_STEP_DATA_NAME);

  async function handleSaveChartName(newName: string): Promise<void> {
    if (newName === viewName) {
      return;
    }
    await withLoadingScreen(
      updateAndRunAnalysisStep(analysis.id, {
        stepId: chartOrTableStep.id,
        stepConfig:
          chartOrTableStep.definition.stepType === PipelineStepType.CHART_VIEW
            ? {
                chartConfig: getPersistedChartViewConfig(visualizationConfig),
                chartName: newName,
              }
            : {
                viewName: newName,
              },
      }),
      'Updating view...'
    );
  }

  const copyView = (): void => {
    withLoadingScreen(copyAnalysisViewSteps(analysis.id, viewSteps[0].id));
  };

  const deleteView = (): void => {
    runConfirmDeleteModal({typeName: 'view'}).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      return withLoadingScreen(
        deleteAnalysisSteps(analysis.id, {
          stepIds: [viewSteps[0].id],
          behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
        })
      );
    });
  };

  const promoteView = (): void => {
    withLoadingScreen(
      addOrReplaceAndRunAnalysisStep(analysis.id, {
        stepType: PipelineStepType.PROMOTE_DATASET,
        stepVersion: 0,
        stepConfig: {},
        stepInput: {
          inputDataFrameId: chartOrTableStep.outputStepDatas[0].dataFrames[0].id,
        },
        stepInputParents: {
          [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
            {
              stepId: chartOrTableStep.id,
              stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
            },
          ],
        },
      })
    );
  };

  return (
    <QueryingChartAndDataFrame
      chartId={chartId}
      inputDataFrameId={sampledOutputDataFrameId ?? inputDataFrameId}
      fullDataFrameId={inputDataFrameId}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({dataFrame, numRows, chart}) => {
        assert(!isInaccessible(dataFrame), 'User does not have access to step input');

        return (
          <LoadingOverlay
            isLoading={
              !viewSteps.some((step) => step.statusCode === PipelineStepStatusCode.FAILED) &&
              viewSteps.some((step) =>
                [
                  PipelineStepStatusCode.BLOCKED,
                  PipelineStepStatusCode.WAITING,
                  PipelineStepStatusCode.RUNNING,
                ].includes(step.statusCode)
              )
            }
            isFaded={true}
          >
            <div
              className={css(
                visualizationStyles.wrapper,
                VISUALIZATION_TYPE_TO_FLEX_GROW[visualizationType] && visualizationStyles.fullHeight
              )}
              {...getTestProps({component: 'VisualizationView', element: 'root'})}
            >
              <div className={css(visualizationStyles.banner)}>
                <StepLockedWarningBanner analysis={analysis} step={chartOrTableStep} />
              </div>
              <VisualizationBlock
                analysis={analysis}
                banner={
                  <>
                    <DataFrameWarningBanner
                      allColumnsJson={dataFrame.allColumnsJson}
                      dataFrame={dataFrame.dataFrameJson}
                      isRandomSubset={false}
                      name={viewName}
                      totalRows={numRows}
                    />
                  </>
                }
                clientStateId={clientStateId}
                dataFrame={dataFrame.dataFrameJson}
                dataFrameId={dataFrame.id}
                viewSteps={viewSteps}
                chart={chart}
                renderFromChart={true}
                visualizationConfig={visualizationConfig}
                title={
                  analysis.permissions.writable ? (
                    <EditableTextField
                      value={viewName}
                      requestChange={handleSaveChartName}
                      dataTestAttrs={{component: 'EditableTextField', element: 'title'}}
                    />
                  ) : (
                    viewName
                  )
                }
                saveToResultsOverflowOption={
                  chartOrTableStep.outputStepDatas[0]?.dataFrames?.length > 0
                    ? getSaveToResultsOverflowOption(
                        analysis,
                        chartOrTableStep,
                        chartOrTableStep.outputStepDatas[0].dataFrames[0].id,
                        onChangeSelectedItem,
                        onOpenStepInitializationView
                      )
                    : null
                }
                onDeleteClicked={deleteView}
                onPromoteClicked={promoteView}
                onCopyClicked={copyView}
                onEditClicked={(): void => {
                  onChangeSelectedItem({
                    type: AnalysisMainViewItemType.EDIT_CHART,
                    stepId: chartOrTableStep.id,
                  });
                }}
              />
            </div>
          </LoadingOverlay>
        );
      }}
    </QueryingChartAndDataFrame>
  );
}

export const visualizationStyles = StyleSheet.create({
  banner: {
    marginBottom: 10,
  },
  wrapper: {
    // There is a box shadow on PresentationalUniversalBlockView so no need to have it here as well,
    // otherwise the shadows overlap making it look like there's one extra-dark shadow.
    ...omit(SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES, 'boxShadow'),
  },
  fullHeight: {
    height: '100%',
  },
});
